import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import DispositionDistribution from 'src/app/interfaces/disposition-distribution';
import { ProceedList } from '../disposition-distribution-proceed/disposition-distribution-proceed.component';
import { DealDispositionsService } from 'src/app/deals/deal-dispositions/deal-dispositions.service';
import {
  RecipientDialogComponent,
  RecipientDialogData,
} from '../recipient-dialog/recipient-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  CreateDispositionDistributionProceedVariables,
  DispositionDistributionProceedInput,
  UpdateDispositionDistributionInput,
  UpdateDispositionDistributionProceedHasMany,
  UpdateDispositionDistributionProceedInput,
} from 'src/app/deals/deal-dispositions/graphql/dispositions';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'disposition-distribution',
    templateUrl: './disposition-distribution.component.html',
    styleUrls: ['./disposition-distribution.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DispositionDistributionComponent {
  @Input() dispositionId: string;
  @Input() distribution: DispositionDistribution;
  @Input() readOnly: boolean = true;
  timeout: any;
  @Output() distributionChanged = new EventEmitter<DispositionDistribution>();
  public proceeds: ProceedList[] = [];
  public proceedSum = 0;
  constructor(
    public dispositionService: DealDispositionsService,
    private dialog: MatDialog
  ) {}
  ngOnInit() {
    this.processProceeds();
  }
  ngOnChanges() {
    this.processProceeds();
  }
  recalculateProceedSum() {
    let sum = 0;
    for (let proceedList of this.proceeds) {
      for (let proceed of proceedList.proceeds) {
        sum += proceed.amount ?? 0;
      }
    }
    this.proceedSum = sum;
  }
  handleDistributionChanged() {
    if (!!this.timeout) {
      window.clearTimeout(this.timeout);
    }
    this.timeout = window.setTimeout(
      () =>
        this.dispositionService.dispositionDistributionChanged(
          this.distribution
        ),
      500
    );
  }
  handleProceedUpdated() {
    //    this.recalculateProceedSum();
    this.dispositionService.dispositionDistributionChanged(this.distribution);
  }
  processProceeds() {
    this.proceedSum = 0;
    for (
      let i = 0;
      i < this.distribution.dispositionDistributionProceeds.length;
      i++
    ) {
      let proceed = this.distribution.dispositionDistributionProceeds[i];
      if (proceed.amount) this.proceedSum += proceed.amount;
      let name = 'Unknown';
      let type = '';
      let id = null;
      if (proceed.fund && proceed.fund.name) {
        name = proceed.fund.name;
        type = 'Fund';
        id = proceed.fund.id;
      } else if (proceed.organization && proceed.organization.name) {
        name = proceed.organization.name;
        type = 'Organization';
        id = proceed.organization.id;
      } else if (proceed.recipient) {
        name = proceed.recipient;
        type = 'Custom';
      }
      let index = this.proceeds.findIndex(
        (proceedList) => proceedList.name === name
      );
      if (index > -1) {
        let proceedIndex = this.proceeds[index].proceeds.findIndex(
          (item) => proceed.id === item.id
        );
        if (proceedIndex > -1) {
          this.proceeds[index].proceeds[proceedIndex] = proceed;
        } else {
          this.proceeds[index].proceeds.push(proceed);
        }
      } else {
        this.proceeds.push({
          name,
          type,
          id,
          disposition_distribution_id: this.distribution.id,
          proceeds: [proceed],
        });
      }
    }
    this.proceeds.sort((a, b) => {
      let aIsCrea = a.name.indexOf('CREA') === -1;
      let bIsCrea = b.name.indexOf('CREA') === -1;
      if (aIsCrea && !bIsCrea) {
        return -1;
      } else if (bIsCrea && !aIsCrea) {
        return 1;
      }

      return a.name === b.name ? 0 : a.name > b.name ? 1 : -1;
    });
  }
  saveDistribution(): void {
    let distributionInput: UpdateDispositionDistributionInput = {
      total: this.distribution.total,
      distribution_date: this.distribution.distribution_date,
      dispositionDistributionProceeds: {
        update: this.mapProceeds(),
      },
    };

    this.dispositionService.updateDispositionDistribution(
      this.distribution.id,
      distributionInput
    );
  }
  mapProceeds(): UpdateDispositionDistributionProceedInput[] {
    let proceedInputs: UpdateDispositionDistributionProceedInput[] = [];
    for (let i = 0; i < this.proceeds.length; i++) {
      let proceedList = this.proceeds[i];
      let mappedProceeds = proceedList.proceeds.map(
        ({ id, disposition_proceed_type_id, amount }) => ({
          id,
          disposition_proceed_type_id,
          amount,
        })
      ) as UpdateDispositionDistributionProceedInput[];
      proceedInputs = [...proceedInputs, ...mappedProceeds];
    }
    return proceedInputs;
  }
  addProceed() {
    let dialogData: RecipientDialogData = {
      type: '',
      organization: null,
      fund: null,
      recipient: '',
    };
    let dialogRef = this.dialog.open(RecipientDialogComponent, {
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((result: RecipientDialogData | null) => {
      let createProceedInput: DispositionDistributionProceedInput = {
        disposition_distribution_id: this.distribution.id,
        fund_id: null,
        organization_id: null,
        recipient: null,
      };
      if (result && result.type && result.type.length) {
        switch (result.type) {
          case 'Organization':
            createProceedInput.organization_id =
              result.organization?.id ?? null;
            break;
          case 'Fund':
            createProceedInput.fund_id = result.fund?.id ?? null;
            break;
          case 'Custom':
            createProceedInput.recipient = result.recipient;
        }

        this.dispositionService.createDispositionDistributionProceed(
          createProceedInput
        );
      }
    });
  }
  deleteDistribution() {
    const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
      ConfirmationModalComponent,
      {
        width: '380px',
        height: '200px',
        data: {
          modalTitle: 'Delete Distribution',
          actionMessage: 'Are you sure you want to delete this distribution?',
          modalInformation: [],
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true,
      }
    );
    ref.afterClosed().subscribe((data) => {
      if (data.clicked === 'Ok') {
        this.dispositionService.deleteDistribution(this.distribution.id);
      }
    });
  }
}
